import { groupBy, uniqBy, sortBy, differenceWith, isEqual, find } from "lodash-es";
import dayjs from "dayjs";
import localizedFormat from "dayjs/plugin/localizedFormat.js";
import relativeTime from "dayjs/plugin/relativeTime.js";
import "dayjs/locale/zh-cn.js";
import "dayjs/locale/en.js";
import "dayjs/locale/fr.js";
function groupByKey(array, key) {
  return groupBy(array, (item) => String(item[key]));
}
function uniqueByKey(array, key) {
  return uniqBy(array, (item) => item[key]);
}
function sortByKey(array, key, order = "asc") {
  const sorted = sortBy(array, [key]);
  return order === "desc" ? sorted.reverse() : sorted;
}
function paginateArray(array, page = 1, pageSize = 10) {
  const startIndex = (page - 1) * pageSize;
  return array.slice(startIndex, startIndex + pageSize);
}
function arrayToTree(array, id = "id", parentId = "parentId", children = "children") {
  const map = {};
  const tree = [];
  for (const item of array) {
    map[item[id]] = { ...item, [children]: [] };
  }
  for (const id2 in map) {
    const item = map[id2];
    if (item[parentId] && map[item[parentId]]) {
      map[item[parentId]][children].push(item);
    } else {
      tree.push(item);
    }
  }
  return tree;
}
function diffArrays(oldArray, newArray, compareKey = null) {
  const result = {
    added: [],
    removed: [],
    modified: []
  };
  const getKey = (item) => compareKey && typeof item === "object" ? item[compareKey] : item;
  result.added = differenceWith(newArray, oldArray, (newItem, oldItem) => isEqual(getKey(newItem), getKey(oldItem)));
  result.removed = differenceWith(oldArray, newArray, (oldItem, newItem) => isEqual(getKey(oldItem), getKey(newItem)));
  if (compareKey) {
    result.modified = newArray.map((newItem) => {
      const oldItem = find(oldArray, (item) => getKey(item) === getKey(newItem));
      if (oldItem && !isEqual(oldItem, newItem)) {
        return { old: oldItem, new: newItem };
      }
      return null;
    }).filter(Boolean);
  }
  return result;
}
class StorageManager {
  constructor(isUseLocalStorage, projectId) {
    this.SESSION_DATA_KEY = "session_timestamp";
    this.isUseLocalStorage = isUseLocalStorage;
    this.projectId = projectId || null;
    this.storage = isUseLocalStorage ? localStorage : localStorage;
    if (!isUseLocalStorage) {
      const sessionKey = this.getProjectKey(this.SESSION_DATA_KEY);
      const now = (/* @__PURE__ */ new Date()).getTime().toString();
      const lastTimestamp = localStorage.getItem(sessionKey);
      if (!lastTimestamp || Number.parseInt(now) - Number.parseInt(lastTimestamp) > 3e4) {
        this.clearSessionItems();
      }
      const updateTimestamp = () => {
        localStorage.setItem(sessionKey, (/* @__PURE__ */ new Date()).getTime().toString());
      };
      updateTimestamp();
      setInterval(updateTimestamp, 5e3);
    }
  }
  getProjectKey(key) {
    if (this.projectId) {
      return `${this.projectId}_${key}`;
    }
    return key;
  }
  clearSessionItems() {
    const keysToRemove = [];
    const sessionPrefix = this.projectId ? `${this.projectId}_session_` : "session_";
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key == null ? void 0 : key.startsWith(sessionPrefix)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => localStorage.removeItem(key));
  }
  setItem(key, value) {
    const baseKey = !this.isUseLocalStorage ? `session_${key}` : key;
    const storageKey = this.getProjectKey(baseKey);
    this.storage.setItem(storageKey, value);
  }
  getItem(key) {
    const baseKey = !this.isUseLocalStorage ? `session_${key}` : key;
    const storageKey = this.getProjectKey(baseKey);
    return this.storage.getItem(storageKey);
  }
  removeItem(key) {
    const baseKey = !this.isUseLocalStorage ? `session_${key}` : key;
    const storageKey = this.getProjectKey(baseKey);
    this.storage.removeItem(storageKey);
  }
  clear() {
    if (this.isUseLocalStorage) {
      const keysToRemove = [];
      const sessionPrefix = this.projectId ? `${this.projectId}_session_` : "session_";
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && !key.startsWith(sessionPrefix)) {
          if (this.projectId && !key.startsWith(`${this.projectId}_`)) {
            continue;
          }
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach((key) => localStorage.removeItem(key));
    } else {
      this.clearSessionItems();
    }
  }
}
class TokenManager {
  constructor(rememberMe, projectId) {
    this.TOKEN_KEY = "access_token";
    this.TOKEN_KEY_REFRESH = "refresh_token";
    this.storageManager = new StorageManager(rememberMe, projectId);
  }
  setToken(token) {
    this.storageManager.setItem(this.TOKEN_KEY, token);
  }
  setRefreshToken(refresh_token) {
    this.storageManager.setItem(this.TOKEN_KEY_REFRESH, refresh_token);
  }
  getToken() {
    return this.storageManager.getItem(this.TOKEN_KEY);
  }
  getRefreshToken() {
    return this.storageManager.getItem(this.TOKEN_KEY_REFRESH);
  }
  removeToken() {
    this.storageManager.removeItem(this.TOKEN_KEY);
  }
  removeRefreshToken() {
    this.storageManager.removeItem(this.TOKEN_KEY_REFRESH);
  }
  static clearAllTokens(projectId) {
    const localTokenManager = new TokenManager(true, projectId);
    const sessionTokenManager = new TokenManager(false, projectId);
    localTokenManager.removeToken();
    localTokenManager.removeRefreshToken();
    sessionTokenManager.removeToken();
    sessionTokenManager.removeRefreshToken();
  }
}
const currencySymbolMap = {
  USD: "$",
  EUR: "€",
  JPY: "¥",
  GBP: "£",
  AUD: "$",
  CAD: "$",
  CHF: "CHF",
  CNY: "¥",
  HKD: "HK$",
  NZD: "NZ$",
  SEK: "kr",
  KRW: "₩",
  SGD: "S$",
  NOK: "kr",
  MXN: "$",
  INR: "₹",
  RUB: "₽",
  ZAR: "R",
  BRL: "R$",
  TRY: "₺"
};
function getCurrencySymbol(currency) {
  return currencySymbolMap[currency] || currencySymbolMap.EUR;
}
dayjs.extend(relativeTime);
dayjs.extend(localizedFormat);
const PRESETS = {
  "en-US": {
    date: "MM/DD/YYYY",
    dateTime: "MM/DD/YYYY hh:mm:ss A"
  },
  "zh-CN": {
    date: "YYYY-MM-DD",
    dateTime: "YYYY-MM-DD HH:mm:ss"
  },
  "fr-FR": {
    date: "DD/MM/YYYY",
    dateTime: "DD/MM/YYYY HH:mm:ss"
  }
};
function getPreset(locale) {
  return PRESETS[locale] || PRESETS["en-US"];
}
function getDayjsLocale(locale) {
  const langCode = locale.split("-")[0].toLowerCase();
  if (langCode === "zh")
    return "zh-cn";
  return "en";
}
function formatDate(date, locale = "en-US", format) {
  const dayjsObj = dayjs(date).locale(getDayjsLocale(locale));
  return dayjsObj.format(format || getPreset(locale).date);
}
function formatDateTime(date, locale = "en-US", format) {
  const dayjsObj = dayjs(date).locale(getDayjsLocale(locale));
  return dayjsObj.format(format || getPreset(locale).dateTime);
}
function getRelativeTime(date, locale = "en-US") {
  const dayjsObj = dayjs(date).locale(getDayjsLocale(locale));
  return dayjsObj.fromNow();
}
function isoString(date) {
  return dayjs(date).toISOString();
}
function formatDateWithTimezone(date, timezone, format = "MMMM D, YYYY") {
  const dayjsObj = dayjs(date).locale("en");
  return `${dayjsObj.format(format)} (${timezone})`;
}
function getTimeZone() {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}
function debounce(func, delay2) {
  let timeoutId;
  return function(...args) {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func.apply(this, args), delay2);
  };
}
function throttle(func, delay2) {
  let lastCall = 0;
  return function(...args) {
    const now = Date.now();
    if (now - lastCall >= delay2) {
      lastCall = now;
      func.apply(this, args);
    }
  };
}
function delay(func, delay2) {
  return function(...args) {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve(func.apply(this, args));
      }, delay2);
    });
  };
}
function once(func) {
  let called = false;
  let result;
  return function(...args) {
    if (!called) {
      called = true;
      result = func.apply(this, args);
    }
    return result;
  };
}
function downloadFile(url, fileName) {
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  a.click();
  URL.revokeObjectURL(url);
}
class HttpError extends Error {
  constructor(message, status, data) {
    super(message);
    this.name = "HttpError";
    this.status = status;
    this.data = data;
  }
}
function buildUrl(url, params) {
  if (!params || Object.keys(params).length === 0) return url;
  const queryString = Object.entries(params).map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`).join("&");
  return url.includes("?") ? `${url}&${queryString}` : `${url}?${queryString}`;
}
async function httpGet(url, options = {}) {
  const { headers = {}, params, timeout } = options;
  const controller = new AbortController();
  if (timeout) {
    setTimeout(() => controller.abort(), timeout);
  }
  const response = await fetch(buildUrl(url, params), {
    method: "GET",
    headers,
    signal: controller.signal
  });
  if (!response.ok) {
    throw new HttpError(
      `HTTP错误 ${response.status}: ${response.statusText}`,
      response.status,
      await response.json().catch(() => null)
    );
  }
  const responseHeaders = {};
  response.headers.forEach((value, key) => {
    responseHeaders[key] = value;
  });
  return {
    data: await response.json(),
    status: response.status,
    headers: responseHeaders
  };
}
async function httpPost(url, data, options = {}) {
  const { headers = {}, params, timeout } = options;
  const controller = new AbortController();
  if (timeout) {
    setTimeout(() => controller.abort(), timeout);
  }
  const response = await fetch(buildUrl(url, params), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...headers
    },
    body: JSON.stringify(data),
    signal: controller.signal
  });
  if (!response.ok) {
    throw new HttpError(
      `HTTP错误 ${response.status}: ${response.statusText}`,
      response.status,
      await response.json().catch(() => null)
    );
  }
  const responseHeaders = {};
  response.headers.forEach((value, key) => {
    responseHeaders[key] = value;
  });
  return {
    data: await response.json(),
    status: response.status,
    headers: responseHeaders
  };
}
async function httpPut(url, data, options = {}) {
  const { headers = {}, params, timeout } = options;
  const controller = new AbortController();
  if (timeout) {
    setTimeout(() => controller.abort(), timeout);
  }
  const response = await fetch(buildUrl(url, params), {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      ...headers
    },
    body: JSON.stringify(data),
    signal: controller.signal
  });
  if (!response.ok) {
    throw new HttpError(
      `HTTP错误 ${response.status}: ${response.statusText}`,
      response.status,
      await response.json().catch(() => null)
    );
  }
  const responseHeaders = {};
  response.headers.forEach((value, key) => {
    responseHeaders[key] = value;
  });
  return {
    data: await response.json(),
    status: response.status,
    headers: responseHeaders
  };
}
async function httpDelete(url, options = {}) {
  const { headers = {}, params, timeout } = options;
  const controller = new AbortController();
  if (timeout) {
    setTimeout(() => controller.abort(), timeout);
  }
  const response = await fetch(buildUrl(url, params), {
    method: "DELETE",
    headers,
    signal: controller.signal
  });
  if (!response.ok) {
    throw new HttpError(
      `HTTP错误 ${response.status}: ${response.statusText}`,
      response.status,
      await response.json().catch(() => null)
    );
  }
  const responseHeaders = {};
  response.headers.forEach((value, key) => {
    responseHeaders[key] = value;
  });
  return {
    data: await response.json(),
    status: response.status,
    headers: responseHeaders
  };
}
async function httpPatch(url, data, options = {}) {
  const { headers = {}, params, timeout } = options;
  const controller = new AbortController();
  if (timeout) {
    setTimeout(() => controller.abort(), timeout);
  }
  const response = await fetch(buildUrl(url, params), {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      ...headers
    },
    body: JSON.stringify(data),
    signal: controller.signal
  });
  if (!response.ok) {
    throw new HttpError(
      `HTTP错误 ${response.status}: ${response.statusText}`,
      response.status,
      await response.json().catch(() => null)
    );
  }
  const responseHeaders = {};
  response.headers.forEach((value, key) => {
    responseHeaders[key] = value;
  });
  return {
    data: await response.json(),
    status: response.status,
    headers: responseHeaders
  };
}
function createHttpClient(baseUrl, defaultOptions = {}) {
  const createUrl = (path) => {
    if (path.startsWith("http://") || path.startsWith("https://")) {
      return path;
    }
    const base = baseUrl.endsWith("/") ? baseUrl.slice(0, -1) : baseUrl;
    const pathWithSlash = path.startsWith("/") ? path : `/${path}`;
    return `${base}${pathWithSlash}`;
  };
  const mergeOptions = (options = {}) => {
    return {
      headers: { ...defaultOptions.headers, ...options.headers },
      params: { ...defaultOptions.params, ...options.params },
      timeout: options.timeout || defaultOptions.timeout
    };
  };
  return {
    get: (path, options) => httpGet(createUrl(path), mergeOptions(options)),
    post: (path, data, options) => httpPost(createUrl(path), data, mergeOptions(options)),
    put: (path, data, options) => httpPut(createUrl(path), data, mergeOptions(options)),
    delete: (path, options) => httpDelete(createUrl(path), mergeOptions(options)),
    patch: (path, data, options) => httpPatch(createUrl(path), data, mergeOptions(options))
  };
}
function truncate(str, maxLength) {
  if (!str)
    return "";
  if (str.length <= maxLength)
    return str;
  return `${str.slice(0, maxLength)}...`;
}
function toCamelCase(str) {
  if (!str)
    return "";
  return str.replace(/[-_](\w)/g, (_, c) => c.toUpperCase()).replace(/^\w/, (c) => c.toLowerCase());
}
function toPascalCase(str) {
  const camelCase = toCamelCase(str);
  return camelCase.charAt(0).toUpperCase() + camelCase.slice(1);
}
function toKebabCase(str) {
  if (!str)
    return "";
  return str.replace(/([a-z])([A-Z])/g, "$1-$2").replace(/[\s_]+/g, "-").toLowerCase();
}
function toSnakeCase(str) {
  if (!str)
    return "";
  return str.replace(/([a-z])([A-Z])/g, "$1_$2").replace(/[\s-]+/g, "_").toLowerCase();
}
function trim(str) {
  if (!str)
    return "";
  return str.trim();
}
function escapeHtml(str) {
  if (!str)
    return "";
  return str.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#039;");
}
function randomString(length, chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789") {
  let result = "";
  const charsLength = chars.length;
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * charsLength));
  }
  return result;
}
function formatMoney(amount, decimals = 2) {
  if (Number.isNaN(amount))
    return "0.00";
  const fixed = amount.toFixed(decimals);
  const [integer, fraction] = fixed.split(".");
  const formatted = integer.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return fraction ? `${formatted}.${fraction}` : formatted;
}
function copyToClipboard(text, successCallback, errorCallback) {
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(() => {
        if (successCallback)
          successCallback();
      }).catch((err) => {
        console.error("剪贴板API复制失败，使用备用方法:", err);
        fallbackCopyTextToClipboard(text, successCallback, errorCallback);
      });
    } else {
      fallbackCopyTextToClipboard(text, successCallback, errorCallback);
    }
  } catch (error) {
    console.error("复制操作异常:", error);
    fallbackCopyTextToClipboard(text, successCallback, errorCallback);
  }
}
function fallbackCopyTextToClipboard(text, successCallback, errorCallback) {
  try {
    const textArea = document.createElement("textarea");
    textArea.value = text;
    textArea.style.position = "fixed";
    textArea.style.left = "-999999px";
    textArea.style.top = "-999999px";
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    const successful = document.execCommand("copy");
    document.body.removeChild(textArea);
    if (successful) {
      if (successCallback)
        successCallback();
    } else {
      if (errorCallback)
        errorCallback(new Error("execCommand复制操作返回false"));
    }
  } catch (err) {
    console.error("备用复制方法失败:", err);
    if (errorCallback)
      errorCallback(err);
  }
}
function isEmpty(value) {
  if (value === null || value === void 0)
    return true;
  if (typeof value === "string")
    return value.trim() === "";
  if (Array.isArray(value))
    return value.length === 0;
  if (typeof value === "object")
    return Object.keys(value).length === 0;
  return false;
}
function isValidEmail(email) {
  const pattern = /^[\w.-]+@[a-z0-9.-]+\.[a-z]{2,6}$/i;
  return pattern.test(email);
}
function isValidPhone(phone) {
  const pattern = /^1[3-9]\d{9}$/;
  return pattern.test(phone);
}
function isValidUrl(url) {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
}
function isStrongPassword(password, options = {
  minLength: 8,
  requireUppercase: true,
  requireLowercase: true,
  requireNumbers: true,
  requireSpecialChars: true
}) {
  if (password.length < options.minLength)
    return false;
  if (options.requireUppercase && !/[A-Z]/.test(password))
    return false;
  if (options.requireLowercase && !/[a-z]/.test(password))
    return false;
  if (options.requireNumbers && !/\d/.test(password))
    return false;
  if (options.requireSpecialChars && !/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password))
    return false;
  return true;
}
function isValidIdCard(idCard) {
  const pattern = /(^\d{15}$)|(^\d{18}$)|(^\d{17}([\dX])$)/i;
  return pattern.test(idCard);
}
export {
  HttpError,
  TokenManager,
  arrayToTree,
  buildUrl,
  copyToClipboard,
  createHttpClient,
  debounce,
  delay,
  diffArrays,
  downloadFile,
  escapeHtml,
  formatDate,
  formatDateTime,
  formatDateWithTimezone,
  formatMoney,
  getCurrencySymbol,
  getRelativeTime,
  getTimeZone,
  groupByKey,
  httpDelete,
  httpGet,
  httpPatch,
  httpPost,
  httpPut,
  isEmpty,
  isStrongPassword,
  isValidEmail,
  isValidIdCard,
  isValidPhone,
  isValidUrl,
  isoString,
  once,
  paginateArray,
  randomString,
  sortByKey,
  throttle,
  toCamelCase,
  toKebabCase,
  toPascalCase,
  toSnakeCase,
  trim,
  truncate,
  uniqueByKey
};
//# sourceMappingURL=index.js.map
